import { useEffect, useRef, useState } from "react";
import { takeDestination } from "@/shared/lib/pendingDestination";
import { useAuth } from "@/shared/contexts/AuthContext";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { useT } from "@/shared/i18n/useT";
import { AuthShell } from "@/shared/components/auth/AuthShell";
import { ac } from "@/shared/components/auth/authCopy";
import {
  checkVerticalAccess,
  type VerticalAccessResult,
} from "@/shared/lib/verticalClient";
import { DEFAULT_VERTICAL } from "@/verticals/registry";
import { resolveProductIntent } from "@/shared/lib/productIntent";
// import { OnboardingCarousel } from "@/shared/components/onboarding/OnboardingCarousel";

const ONBOARDING_SEEN_KEY = "empatiq_onboarding_seen";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAVY = "#0c1a33";

type Step =
  | { kind: "email" }
  | { kind: "has_password"; email: string }
  | { kind: "needs_password"; email: string; providers: string[] }
  | { kind: "no_account" };

type MatchedOrg = NonNullable<VerticalAccessResult["org"]>;

export function LoginPage() {
  const { signIn, signInWithGoogle, resetPassword, user } = useAuth();
  const { t, locale } = useLanguage();
  const tr = useT();
  const [step, setStep] = useState<Step>({ kind: "email" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  // Login never gates — the account's real vertical is enforced server-side.
  // The resolved org just themes the brand panel (a returning-user nicety).
  // `resolvedVertical` is null until a check completes, so the panel stays
  // neutral (no vertical claimed) and then transitions into the theme.
  // On a product front door, theme
  // the rail as that product's vertical until the email resolves an org.
  const [productIntent] = useState(resolveProductIntent);
  const defaultDestination = "/dashboard";
  const signupPath = productIntent ? `/${productIntent.id}/signup` : "/signup";
  const [resolvedVertical, setResolvedVertical] = useState<string | null>(
    () => productIntent?.vertical ?? null,
  );
  const [matchedOrg, setMatchedOrg] = useState<MatchedOrg | null>(null);
  const lastChecked = useRef<string>("");

  const themeFromEmail = async (raw: string) => {
    const e = raw.trim().toLowerCase();
    if (!e || !EMAIL_RE.test(e) || e === lastChecked.current) return;
    lastChecked.current = e;
    const res = await checkVerticalAccess(e);
    setMatchedOrg(res.org ?? null);
    setResolvedVertical(
      res.org?.vertical ??
        res.accountVertical ??
        productIntent?.vertical ??
        DEFAULT_VERTICAL,
    );
  };

  // First-run walkthrough, shown once before the login form (app-style intro).
  const [showOnboarding, setShowOnboarding] = useState<boolean>(() => {
    try {
      if (localStorage.getItem(ONBOARDING_SEEN_KEY)) return false;
      // App-style walkthrough is for the native app / small screens only —
      // skip it on desktop web.
      const isDesktopWeb = window.innerWidth >= 1024;
      return !isDesktopWeb;
    } catch {
      return false;
    }
  });
  const dismissOnboarding = () => {
    try {
      localStorage.setItem(ONBOARDING_SEEN_KEY, "1");
    } catch {
      /* ignore */
    }
    setShowOnboarding(false);
  };

  useEffect(() => {
    if (!user) return;
    let redirectTo = takeDestination() ?? defaultDestination;
    try {
      const stored = sessionStorage.getItem("loginRedirectTo");
      if (stored) {
        redirectTo = stored;
        sessionStorage.removeItem("loginRedirectTo");
      }
    } catch {
      /* ignore */
    }
    (window as any).navigate(redirectTo);
  }, [defaultDestination, user]);

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const lower = email.trim().toLowerCase();
    lastChecked.current = lower;
    try {
      // Look up the account and resolve the theme in parallel.
      const [data, access] = await Promise.all([
        fetch("/api/auth/lookup-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: lower }),
        }).then((r) => r.json()) as Promise<{
          status: "has_password" | "needs_password" | "no_account";
          email?: string;
          providers?: string[];
        }>,
        checkVerticalAccess(lower),
      ]);
      setMatchedOrg(access.org ?? null);
      setResolvedVertical(
        access.org?.vertical ??
          access.accountVertical ??
          productIntent?.vertical ??
          DEFAULT_VERTICAL,
      );
      if (data.status === "has_password")
        setStep({ kind: "has_password", email: data.email ?? email });
      else if (data.status === "needs_password")
        setStep({
          kind: "needs_password",
          email: data.email ?? email,
          providers: data.providers ?? [],
        });
      else setStep({ kind: "no_account" });
    } catch {
      setError(tr("ui.loginPage.somethingWentWrong"));
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error } = await signIn(email, password);
    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }
    let redirectTo = takeDestination() ?? defaultDestination;
    try {
      const stored = sessionStorage.getItem("loginRedirectTo");
      if (stored) {
        redirectTo = stored;
        sessionStorage.removeItem("loginRedirectTo");
      }
    } catch {
      /* ignore */
    }
    (window as any).navigate(redirectTo);
  };

  // Accounts without a password (invited / OAuth-only) get an emailed
  // set-password link — the recovery flow — never an inline password form:
  // proof of inbox ownership is what authorizes setting the password.
  const handleSendSetupLink = async () => {
    if (step.kind !== "needs_password") return;
    setError(null);
    setLoading(true);
    const { error } = await resetPassword(step.email);
    if (error) setError(error.message);
    else setLinkSent(true);
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    const { error } = await signInWithGoogle();
    if (error) setError(error.message);
  };

  const handleBackToEmail = () => {
    setStep({ kind: "email" });
    setPassword("");
    setLinkSent(false);
    setError(null);
  };

  const labelCls = "au-mono block text-[0.625rem] mb-1.5";
  const inputCls =
    "min-h-12 w-full rounded-[6px] border border-[rgba(17,24,39,0.16)] bg-white px-3.5 py-2.5 text-[0.9rem] text-[#111827] placeholder:text-[#98a2b3] outline-none transition-all hover:border-[rgba(17,24,39,0.28)] focus:border-[var(--auth-accent)] focus:ring-[3px] focus:ring-[var(--auth-accent-soft)]";
  const accentBtn =
    "min-h-12 w-full cursor-pointer rounded-[6px] bg-[var(--auth-accent)] py-2.5 text-[0.82rem] font-semibold tracking-wide text-white transition-all hover:brightness-95 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60";
  const labelStyle = { color: "rgba(12,26,51,0.6)" };
  const isEmailStep = step.kind === "email";

  const googleButton = (
    <button
      type="button"
      onClick={handleGoogleSignIn}
      className="flex min-h-12 w-full cursor-pointer items-center justify-center gap-3 rounded-[6px] border border-[rgba(17,24,39,0.14)] bg-white px-4 py-2.5 text-[0.85rem] font-medium transition-colors hover:bg-[#f7f8fa] active:scale-[0.99]"
      style={{ color: NAVY }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
      {t.auth.continueWithGoogle}
    </button>
  );

  // if (showOnboarding && !user) {
  //   return <OnboardingCarousel locale={locale} onDone={dismissOnboarding} />;
  // }

  return (
    <AuthShell
      locale={locale}
      vertical={resolvedVertical ?? undefined}
      org={matchedOrg}
      product={
        !matchedOrg && productIntent
          ? { accent: productIntent.accent, name: productIntent.label }
          : undefined
      }
      railTitle={
        !matchedOrg && productIntent
          ? productIntent.tagline
          : ac("loginTitle", locale)
      }
      railFooter={ac("trust", locale)}
    >
      <h1
        className="au-display text-2xl font-semibold mb-1"
        style={{ color: NAVY }}
      >
        {step.kind === "needs_password"
          ? tr("ui.loginPage.setPasswordTitle")
          : t.auth.loginTitle}
      </h1>
      <p className="text-sm mb-8" style={{ color: "rgba(12,26,51,0.55)" }}>
        {step.kind === "needs_password"
          ? tr("ui.loginPage.needsPasswordSubtitle")
          : step.kind === "has_password"
            ? tr("ui.loginPage.hasPasswordSubtitle")
            : t.auth.loginSubtitle}
      </p>

      {/* Social sign-in — only on the email step */}
      {isEmailStep && (
        <>
          {googleButton}

          <div className="flex items-center gap-3 my-6">
            <div
              className="flex-1 h-px"
              style={{ background: "rgba(12,26,51,0.12)" }}
            />
            <span
              className="au-mono text-[0.5625rem]"
              style={{ color: "rgba(12,26,51,0.4)" }}
            >
              {t.auth.or}
            </span>
            <div
              className="flex-1 h-px"
              style={{ background: "rgba(12,26,51,0.12)" }}
            />
          </div>
        </>
      )}

      {/* Step 1 — email */}
      {step.kind === "email" && (
        <form onSubmit={handleEmailContinue} className="space-y-4">
          <div>
            <label htmlFor="email" className={labelCls} style={labelStyle}>
              {t.auth.email}
            </label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={(e) => void themeFromEmail(e.target.value)}
              required
              className={inputCls}
            />
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-[3px] p-3">
              {error}
            </div>
          )}
          <button type="submit" disabled={loading} className={accentBtn}>
            {loading
              ? tr("ui.loginPage.checking")
              : tr("ui.loginPage.continue")}
          </button>
        </form>
      )}

      {/* Step 2a — has password, normal sign in */}
      {step.kind === "has_password" && (
        <form onSubmit={handleSignIn} className="space-y-4">
          <div>
            <label className={labelCls} style={labelStyle}>
              {t.auth.email}
            </label>
            <input
              type="email"
              value={step.email}
              disabled
              className={inputCls + " bg-stone-50 text-stone-500"}
            />
          </div>
          <div>
            <label htmlFor="password" className={labelCls} style={labelStyle}>
              {t.auth.password}
            </label>
            <input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              className={inputCls}
            />
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleBackToEmail}
              className="text-xs cursor-pointer"
              style={{ color: "rgba(12,26,51,0.5)" }}
            >
              {tr("ui.loginPage.differentEmail")}
            </button>
            <button
              type="button"
              onClick={() => (window as any).navigate("/forgot-password")}
              className="text-xs font-medium cursor-pointer"
              style={{ color: "var(--auth-accent)" }}
            >
              {tr("ui.loginPage.forgotPassword")}
            </button>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-[3px] p-3">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} className={accentBtn}>
            {loading ? t.auth.signingIn : t.auth.signIn}
          </button>
        </form>
      )}

      {/* Step 2b — account without a password (invited / OAuth-only).
          Activation goes through an emailed set-password link; the inbox is
          the proof of ownership. OAuth accounts are pointed back at their
          provider first. */}
      {step.kind === "needs_password" && (
        <div className="space-y-4">
          {linkSent ? (
            <div
              className="rounded-[3px] bg-white p-4"
              style={{ boxShadow: "inset 0 0 0 1px var(--auth-accent-soft)" }}
            >
              <p className="text-sm font-semibold" style={{ color: NAVY }}>
                {tr("ui.loginPage.linkSentTitle")}
              </p>
              <p
                className="text-xs mt-1"
                style={{ color: "rgba(12,26,51,0.6)" }}
              >
                {tr("ui.loginPage.linkSentBody", { email: step.email })}
              </p>
            </div>
          ) : (
            <>
              <div
                className="rounded-[3px] bg-white p-4"
                style={{ boxShadow: "inset 0 0 0 1px var(--auth-accent-soft)" }}
              >
                <p className="text-sm font-semibold" style={{ color: NAVY }}>
                  {tr("ui.loginPage.welcome")}
                </p>
                <p
                  className="text-xs mt-1"
                  style={{ color: "rgba(12,26,51,0.6)" }}
                >
                  {step.providers.includes("google")
                    ? tr("ui.loginPage.oauthAccountHint")
                    : tr("ui.loginPage.noPasswordYet")}
                </p>
              </div>
              <div>
                <label className={labelCls} style={labelStyle}>
                  {t.auth.email}
                </label>
                <input
                  type="email"
                  value={step.email}
                  disabled
                  className={inputCls + " bg-stone-50 text-stone-500"}
                />
              </div>

              {step.providers.includes("google") && (
                <>
                  {googleButton}
                  <div className="flex items-center gap-3 my-2">
                    <div
                      className="flex-1 h-px"
                      style={{ background: "rgba(12,26,51,0.12)" }}
                    />
                    <span
                      className="au-mono text-[0.5625rem]"
                      style={{ color: "rgba(12,26,51,0.4)" }}
                    >
                      {t.auth.or}
                    </span>
                    <div
                      className="flex-1 h-px"
                      style={{ background: "rgba(12,26,51,0.12)" }}
                    />
                  </div>
                </>
              )}

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-[3px] p-3">
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={handleSendSetupLink}
                disabled={loading}
                className={accentBtn}
              >
                {loading
                  ? tr("ui.loginPage.sendingLink")
                  : tr("ui.loginPage.emailSetupLink")}
              </button>
            </>
          )}

          <div className="flex justify-start">
            <button
              type="button"
              onClick={handleBackToEmail}
              className="text-xs cursor-pointer"
              style={{ color: "rgba(12,26,51,0.5)" }}
            >
              {tr("ui.loginPage.differentEmail")}
            </button>
          </div>
        </div>
      )}

      {/* Step 2c — no account found */}
      {step.kind === "no_account" && (
        <div className="space-y-4">
          <div
            className="rounded-[3px] bg-white p-4"
            style={{ boxShadow: "inset 0 0 0 1px rgba(12,26,51,0.12)" }}
          >
            <p className="text-sm font-semibold mb-1" style={{ color: NAVY }}>
              {tr("ui.loginPage.emailNotFound")}
            </p>
            <p className="text-xs" style={{ color: "rgba(12,26,51,0.6)" }}>
              {tr("ui.loginPage.checkAddress")}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => (window as any).navigate(signupPath)}
              className={accentBtn}
            >
              {tr("ui.loginPage.createAccount")}
            </button>
            <button
              type="button"
              onClick={handleBackToEmail}
              className="w-full py-2.5 rounded-[3px] border border-[rgba(12,26,51,0.18)] bg-white text-[0.85rem] font-semibold hover:bg-[rgba(12,26,51,0.03)] transition-colors cursor-pointer"
              style={{ color: NAVY }}
            >
              {tr("ui.loginPage.tryDifferentEmail")}
            </button>
          </div>
        </div>
      )}

      {isEmailStep && (
        <p
          className="text-center text-sm mt-6"
          style={{ color: "rgba(12,26,51,0.55)" }}
        >
          {t.auth.noAccount}{" "}
          <button
            onClick={() => (window as any).navigate(signupPath)}
            className="font-medium cursor-pointer"
            style={{ color: "var(--auth-accent)" }}
          >
            {t.auth.signUpLink}
          </button>
        </p>
      )}
    </AuthShell>
  );
}
